/**
 * SnakePatternNode - Maps patterns to a grid using serpentine traversal
 *
 * Receives a 1D pattern and maps values to cells in a 2D grid following
 * a "snake" path. This visualization reveals structures in patterns that
 * aren't obvious in linear form.
 *
 * Endpoints:
 * - pattern (input): 1D array of values to visualize
 * - grid (output): 2D array with values mapped via snake traversal
 * - nRows, nCols (io): grid dimensions (odd numbers, 3-21)
 * - layout (io): snake traversal direction
 */

import { BaseNode } from '@nodes/BaseNode';
import { Domain } from '@nodes/Endpoint';
import { Dependencies } from '@nodes/Dependencies';
import { NodeDef } from '@runtime/IRuntime';
import { Payload } from '@graph/Payload';
import { params } from '@/nodes/TypedEndpoints';

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

export type SnakeLayout =
  | 'TL->BR'
  | 'BR->TL'
  | 'TL->C, BR->C';

const LAYOUT_OPTIONS: SnakeLayout[] = [
  'TL->BR',
  'BR->TL',
  'TL->C, BR->C'
];

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────

/**
 * Ensure value is odd and within range
 */
function clampToOdd(value: number, min: number, max: number): number {
  let v = Math.max(min, Math.min(max, value));
  if (v % 2 === 0) {
    v = v - 1 >= min ? v - 1 : v + 1;
  }
  return v;
}

/**
 * Generate snake traversal order from top-left to bottom-right
 */
function generateSnakeTopLeftToBottomRight(nRows: number, nCols: number): [number, number][] {
  const positions: [number, number][] = [];

  for (let row = 0; row < nRows; row++) {
    if (row % 2 === 0) {
      for (let col = 0; col < nCols; col++) {
        positions.push([row, col]);
      }
    } else {
      for (let col = nCols - 1; col >= 0; col--) {
        positions.push([row, col]);
      }
    }
  }

  return positions;
}

/**
 * Generate snake traversal order from bottom-right to top-left
 */
function generateSnakeBottomRightToTopLeft(nRows: number, nCols: number): [number, number][] {
  return generateSnakeTopLeftToBottomRight(nRows, nCols).reverse();
}

/**
 * Generate dual snake traversal meeting at center
 */
function generateDualSnakeToCenter(
  nRows: number,
  nCols: number
): { snakeA: [number, number][]; snakeB: [number, number][]; center: [number, number] } {
  const totalCells = nRows * nCols;
  const centerRow = Math.floor(nRows / 2);
  const centerCol = Math.floor(nCols / 2);
  const center: [number, number] = [centerRow, centerCol];

  const fullSnake = generateSnakeTopLeftToBottomRight(nRows, nCols);
  const halfPoint = Math.floor(totalCells / 2);

  const snakeA = fullSnake.slice(0, halfPoint);
  const snakeB = fullSnake.slice(halfPoint + 1).reverse();

  return { snakeA, snakeB, center };
}

// ─────────────────────────────────────────────────────────────
// Endpoints
// ─────────────────────────────────────────────────────────────

const endpoints = params({
  pattern: { dataType: 'array',  primary: true },
  grid: { dataType: 'array', isOutput: true },
  nRows: { isOutput: true, name: 'Rows', value: 5, min: 3, max: 21, step: 2 },
  nCols: { isOutput: true, name: 'Cols', value: 5, min: 3, max: 21, step: 2 },
  layout: { dataType: 'string',  isOutput: true, value: 'TL->C, BR->C', options: LAYOUT_OPTIONS },
});

// ─────────────────────────────────────────────────────────────
// Node
// ─────────────────────────────────────────────────────────────

export class SnakePatternNode extends BaseNode<typeof endpoints> {
  
  
  initialize(): void {
     this.computeAndEmitGrid();
  }

  onParamChanged(payload: Payload, endpointId: string): void {
    // Ensure nRows/nCols stay odd
    if (endpointId === 'nRows') {
      const current = this.params.nRows.value;
      const clamped = clampToOdd(current, 3, 21);
      if (clamped !== current) {
        this.params.nRows.set(clamped);
      }
    }
    if (endpointId === 'nCols') {
      const current = this.params.nCols.value;
      const clamped = clampToOdd(current, 3, 21);
      if (clamped !== current) {
        this.params.nCols.set(clamped);
      }
    }

    // Recompute on any change
    this.computeAndEmitGrid();
  }

  // ─────────────────────────────────────────────────────────────
  // Grid Computation
  // ─────────────────────────────────────────────────────────────

  private computeAndEmitGrid(): void {
    const pattern = this.getPatternArray();
    const nRows = this.params.nRows.value;
    const nCols = this.params.nCols.value;
    const layout = this.params.layout.value as SnakeLayout;

    // Create empty grid (row-major: grid[row][col])
    const grid: (number | null)[][] = Array(nRows)
      .fill(null)
      .map(() => Array(nCols).fill(null));

    if (pattern.length === 0) {
      this.params.grid.set(grid);
      return;
    }

    switch (layout) {
      case 'TL->BR':
        this.fillGrid(grid, generateSnakeTopLeftToBottomRight(nRows, nCols), pattern);
        break;

      case 'BR->TL':
        this.fillGrid(grid, generateSnakeBottomRightToTopLeft(nRows, nCols), pattern);
        break;

      case 'TL->C, BR->C':
        this.fillDualSnakeGrid(grid, nRows, nCols, pattern);
        break;
    }

    this.params.grid.set(grid);
  }

  private fillGrid(grid: (number | null)[][], positions: [number, number][], pattern: number[]): void {
    const patternLen = pattern.length;
    positions.forEach(([row, col], index) => {
      const patternIndex = index % patternLen;
      grid[row][col] = pattern[patternIndex];
    });
  }

  private fillDualSnakeGrid(grid: (number | null)[][], nRows: number, nCols: number, pattern: number[]): void {
    const { snakeA, snakeB, center } = generateDualSnakeToCenter(nRows, nCols);
    const patternLen = pattern.length;

    // Fill snake A
    snakeA.forEach(([row, col], index) => {
      grid[row][col] = pattern[index % patternLen];
    });

    // Fill center
    const [centerRow, centerCol] = center;
    grid[centerRow][centerCol] = pattern[0];

    // Fill snake B
    snakeB.forEach(([row, col], index) => {
      grid[row][col] = pattern[index % patternLen];
    });
  }

  private getPatternArray(): number[] {
    const data = this.params.pattern.value;
    if (!data) return [];
    return Array.isArray(data) ? data : [];
  }

  // ─────────────────────────────────────────────────────────────
  // Accessors
  // ─────────────────────────────────────────────────────────────

  get grid(): (number | null)[][] {
    return this.params.grid.value;
  }

  get nRows(): number {
    return this.params.nRows.value;
  }

  get nCols(): number {
    return this.params.nCols.value;
  }

  get layout(): SnakeLayout {
    return this.params.layout.value as SnakeLayout;
  }

  /**
   * Get value at specific grid position
   */
  getValueAt(row: number, col: number): number | null {
    const g = this.grid;
    if (row >= 0 && row < this.nRows && col >= 0 && col < this.nCols) {
      return g[row]?.[col] ?? null;
    }
    return null;
  }

  /**
   * Get a string representation of the grid for debugging
   */
  toString(): string {
    const g = this.grid;
    if (!g.length) return '(empty)';

    let maxWidth = 2;
    for (const row of g) {
      for (const val of row) {
        if (val !== null) {
          maxWidth = Math.max(maxWidth, String(val).length);
        }
      }
    }

    return g
      .map(row =>
        row
          .map(v => (v !== null ? String(v).padStart(maxWidth) : '.'.padStart(maxWidth)))
          .join(' ')
      )
      .join('\n');
  }
}

// ─────────────────────────────────────────────────────────────
// Node Definition
// ─────────────────────────────────────────────────────────────

export const snakePatternNodeDef: NodeDef<SnakePatternNode> = {
  type: 'SnakePattern',
  info: 'Maps a 1D pattern to a 2D grid using serpentine traversal, revealing spatial structure.',
  domain: Domain.Data,
  shortName: 'snake',
  endpoints,
  ctor: SnakePatternNode
};
